/**
 * Service ticket (AppSheet `Service Log :: Status`) vocabulary — the ONE definition
 * shared by the client and the default functions codebase.
 *
 * NICK'S RULING, 2026-09-22:
 *  - "8 Declined" is a TERMINAL outcome: the customer declined, never responded, or we
 *    lost the contract. We tried; no work was performed, nothing is billed. It is NOT a
 *    duplicate and NOT an internal cancel, and it is measured separately from both.
 *  - "9 Cancelled" stays the cancel outcome (duplicates, internal cancels).
 *  - The old stage-8 cancel value is RETIRED (0 live tickets used it, measured). It is not
 *    in this list, and a guard test fails if it reappears anywhere in source.
 *
 * Both closed-without-billing outcomes are terminal, never billable, never "active" and
 * never "needs attention".
 */

export const BILLED_STATUS = '7 Billed' as const;
export const DECLINED_STATUS = '8 Declined' as const;
export const CANCELLED_STATUS = '9 Cancelled' as const;

/** Every Status a service ticket may hold, in lifecycle order. */
export const SERVICE_TICKET_STATUSES = [
  '0 System Generated',
  '0 User Generated',
  '1.1 To Quote',
  '1.2 Quote Sent',
  '1.3 Quote Approved',
  '1.4 Preparing Job',
  '1.5 Ready to Schedule',
  '2 Attempting to Schedule',
  '3 Confirmed',
  '4.1 Dispatched',
  '4.2 In Progress',
  '4.3 Reports Due',
  '5 Completed',
  '6 Reviewed',
  BILLED_STATUS,
  DECLINED_STATUS,
  CANCELLED_STATUS,
] as const;

export type ServiceTicketStatusValue = (typeof SERVICE_TICKET_STATUSES)[number];

/**
 * The statuses a PERSON may open a ticket at (Create Ticket's Status picker, and the
 * interactive create callable's allow-list). DILLAN'S RULING, 2026-10-22: from "1.1 To Quote"
 * through "4.2 In Progress". Deliberately NOT offered: the "0" statuses (system / legacy),
 * "4.3 Reports Due" onward ("5 Completed" and "6 Reviewed" feed billing — a ticket must not be
 * born already done), and every terminal status.
 *
 * An allow-list on purpose: a status added to the pipeline later is not openable until
 * someone decides it is.
 */
export const PERSON_OPENING_STATUSES = [
  '1.1 To Quote',
  '1.2 Quote Sent',
  '1.3 Quote Approved',
  '1.4 Preparing Job',
  '1.5 Ready to Schedule',
  '2 Attempting to Schedule',
  '3 Confirmed',
  '4.1 Dispatched',
  '4.2 In Progress',
] as const satisfies readonly ServiceTicketStatusValue[];

/**
 * Opening statuses at which the work is already with a person in the field, so a Technician is required: the
 * Create Ticket form asks for one and the create callable refuses without one (gauntlet, 2026-10-22).
 */
export const TECHNICIAN_REQUIRED_OPENING_STATUSES: readonly ServiceTicketStatusValue[] = [
  '4.1 Dispatched',
  '4.2 In Progress',
];

/** The status a new ticket opens at when nobody picks one. */
export const DEFAULT_OPENING_STATUS: ServiceTicketStatusValue = PERSON_OPENING_STATUSES[0];

/** Terminal, and never billed. */
export const CLOSED_UNBILLED_STATUSES: ReadonlySet<string> = new Set<string>([
  DECLINED_STATUS,
  CANCELLED_STATUS,
]);

/** How a closed ticket ended. Each is its own bucket — never merge declined into cancelled. */
export type ClosedOutcome = 'billed' | 'declined' | 'cancelled';

function norm(status: unknown): string | null {
  if (typeof status !== 'string') return null;
  const s = status.trim();
  return s === '' ? null : s;
}

/** True only for "8 Declined". */
export function isDeclinedStatus(status: unknown): boolean {
  return norm(status) === DECLINED_STATUS;
}

/** True only for "9 Cancelled". A declined ticket is NOT cancelled. */
export function isCancelledStatus(status: unknown): boolean {
  return norm(status) === CANCELLED_STATUS;
}

/**
 * True when the ticket is closed WITHOUT billing — the question every billing gate asks.
 *
 * FAILS CLOSED: besides the two named values, ANY status whose stage digit is 8 or 9 counts
 * (the retired stage-8 cancel value in an old archive row, or a value AppSheet adds before
 * OfficeHub knows it). A billing gate that let an unrecognised closed status through would
 * bill a job that was never done; one that refuses it costs an operator a reopen.
 */
export function isClosedUnbilledStatus(status: unknown): boolean {
  const s = norm(status);
  if (s === null) return false;
  if (CLOSED_UNBILLED_STATUSES.has(s)) return true;
  return /^[89](?![0-9])/.test(s);
}

/** True when no further work or billing happens: billed, declined or cancelled. */
export function isTerminalStatus(status: unknown): boolean {
  return norm(status) === BILLED_STATUS || isClosedUnbilledStatus(status);
}

/**
 * Which closed bucket a status belongs to, or `null` when it is not one of the three named
 * closed statuses. Deliberately exact: an unrecognised stage-8/9 value returns `null` so a
 * report counts it as unknown rather than guessing declined or cancelled.
 */
export function closedOutcome(status: unknown): ClosedOutcome | null {
  const s = norm(status);
  if (s === BILLED_STATUS) return 'billed';
  if (s === DECLINED_STATUS) return 'declined';
  if (s === CANCELLED_STATUS) return 'cancelled';
  return null;
}
